import * as THREE from 'three';

/**
 * Day/night tinting driven by the sim's dayPhase (0..1 over a 20-min day).
 * Pure keyframe lerps on light/fog/sky colors — the sim decides sleep,
 * render just sets the mood.
 */
interface Stop {
  phase: number;
  sky: THREE.Color;
  sun: THREE.Color;
  sunIntensity: number;
  hemiIntensity: number;
}

const STOPS: Stop[] = [
  { phase: 0.0, sky: new THREE.Color('#E8B36A'), sun: new THREE.Color('#FFC97E'), sunIntensity: 1.5, hemiIntensity: 0.7 }, // dawn
  { phase: 0.12, sky: new THREE.Color('#79B7E8'), sun: new THREE.Color('#FFF4D6'), sunIntensity: 2.4, hemiIntensity: 1.0 }, // morning
  { phase: 0.55, sky: new THREE.Color('#79B7E8'), sun: new THREE.Color('#FFF4D6'), sunIntensity: 2.4, hemiIntensity: 1.0 }, // afternoon
  { phase: 0.7, sky: new THREE.Color('#D9825F'), sun: new THREE.Color('#FF9E5E'), sunIntensity: 1.5, hemiIntensity: 0.7 }, // dusk
  { phase: 0.78, sky: new THREE.Color('#1B2A4A'), sun: new THREE.Color('#A9C4FF'), sunIntensity: 0.5, hemiIntensity: 0.4 }, // night
  { phase: 0.94, sky: new THREE.Color('#1B2A4A'), sun: new THREE.Color('#A9C4FF'), sunIntensity: 0.5, hemiIntensity: 0.4 }, // deep night
  { phase: 1.0, sky: new THREE.Color('#E8B36A'), sun: new THREE.Color('#FFC97E'), sunIntensity: 1.5, hemiIntensity: 0.7 }, // wrap to dawn
];

export class DayNight {
  private readonly skyColor = new THREE.Color();

  constructor(
    private readonly scene: THREE.Scene,
    private readonly sun: THREE.DirectionalLight,
    private readonly hemi: THREE.HemisphereLight,
  ) {}

  apply(phase: number): void {
    const p = ((phase % 1) + 1) % 1;
    let a = STOPS[0] as Stop;
    let b = STOPS[STOPS.length - 1] as Stop;
    for (let i = 0; i < STOPS.length - 1; i++) {
      const s0 = STOPS[i] as Stop;
      const s1 = STOPS[i + 1] as Stop;
      if (p >= s0.phase && p <= s1.phase) {
        a = s0;
        b = s1;
        break;
      }
    }
    const span = Math.max(1e-4, b.phase - a.phase);
    const t = (p - a.phase) / span;

    this.skyColor.copy(a.sky).lerp(b.sky, t);
    if (this.scene.background instanceof THREE.Color) {
      this.scene.background.copy(this.skyColor);
    }
    if (this.scene.fog) this.scene.fog.color.copy(this.skyColor);
    this.sun.color.copy(a.sun).lerp(b.sun, t);
    this.sun.intensity = a.sunIntensity + (b.sunIntensity - a.sunIntensity) * t;
    this.hemi.intensity = a.hemiIntensity + (b.hemiIntensity - a.hemiIntensity) * t;
  }
}
